import { Component, OnInit, OnDestroy, NgZone, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { TourService, TourState } from 'app/core/services/tour.service';

const PRIMARY    = 'var(--theme-primary, #008080)';
const PRIMARY_DK = 'var(--theme-primary-dark, #006666)';

@Component({
  selector: 'app-tour-overlay',
  template: '',
  encapsulation: ViewEncapsulation.None,
})
export class TourOverlayComponent implements OnInit, OnDestroy {

  // All three elements are siblings directly on document.body.
  private backdrop: HTMLDivElement;   // dark vignette (via box-shadow) + click-away
  private ring:     HTMLDivElement;   // transparent spotlight with huge box-shadow
  private card:     HTMLDivElement;   // tooltip

  // card sub-elements
  private titleEl:  HTMLElement;
  private descEl:   HTMLElement;
  private dotsEl:   HTMLElement;
  private badgeEl:  HTMLElement;
  private prevBtn:  HTMLButtonElement;
  private nextBtn:  HTMLButtonElement;
  private arrowEl:  HTMLElement;

  private sub: Subscription;
  private stepId = 0;

  private readonly PAD  = 10;
  private readonly TW   = 320;
  private readonly TH   = 230;
  private readonly SB   = 260;   // sidebar width

  constructor(private svc: TourService, private zone: NgZone) {}

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.build();
    this.sub = this.svc.state$.subscribe(s => this.onState(s));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.backdrop?.remove();
    this.ring?.remove();
    this.card?.remove();
  }

  // ─── Build DOM (once) ────────────────────────────────────────────────────────

  private build(): void {
    // ── Backdrop: covers whole screen, captures click-away ───────────────────
    this.backdrop = document.createElement('div');
    css(this.backdrop, {
      display: 'none', position: 'fixed', inset: '0',
      zIndex: '999998', background: 'transparent', cursor: 'pointer',
    });
    this.backdrop.addEventListener('click', () =>
      this.zone.run(() => this.svc.end()));

    // ── Ring: transparent spotlight, box-shadow = dark vignette ─────────────
    this.ring = document.createElement('div');
    css(this.ring, {
      display: 'none', position: 'fixed',
      borderRadius: '8px', pointerEvents: 'none',
      boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
      outline: `2.5px solid ${PRIMARY}`, outlineOffset: '1px',
      zIndex: '999999',
      transition: 'top .3s ease, left .3s ease, width .3s ease, height .3s ease',
    });

    // ── Card ─────────────────────────────────────────────────────────────────
    this.card = document.createElement('div');
    css(this.card, {
      display: 'none', position: 'fixed', zIndex: '1000000',
      width: '320px', background: '#fff', borderRadius: '14px',
      boxShadow: '0 20px 60px rgba(0,0,0,.28), 0 4px 16px rgba(0,0,0,.14)',
      overflow: 'hidden', pointerEvents: 'all',
      fontFamily: 'Montserrat, Helvetica Neue, sans-serif',
      transition: 'top .3s ease, left .3s ease',
    });
    this.card.addEventListener('click', e => e.stopPropagation());

    this.buildCard();

    document.body.append(this.backdrop, this.ring, this.card);
  }

  private buildCard(): void {
    // ── Arrow ────────────────────────────────────────────────────────────────
    this.arrowEl = document.createElement('div');
    css(this.arrowEl, { position: 'absolute', width: '0', height: '0', pointerEvents: 'none' });
    this.card.appendChild(this.arrowEl);

    // ── Header ───────────────────────────────────────────────────────────────
    const head = document.createElement('div');
    css(head, {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '14px 16px 12px',
      background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DK})`,
      color: '#fff',
    });

    const icon = document.createElement('i');
    icon.className = 'material-icons';
    icon.textContent = 'explore';
    css(icon, { fontSize: '20px', opacity: '.9', flexShrink: '0' });

    this.titleEl = document.createElement('span');
    css(this.titleEl, { flex: '1', fontSize: '14px', fontWeight: '700', letterSpacing: '.2px' });

    const xBtn = document.createElement('button') as HTMLButtonElement;
    css(xBtn, {
      background: 'rgba(255,255,255,.18)', border: 'none', borderRadius: '6px',
      color: '#fff', width: '26px', height: '26px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', flexShrink: '0',
    });
    xBtn.innerHTML = '<i class="material-icons" style="font-size:16px">close</i>';
    xBtn.addEventListener('click', () => this.zone.run(() => this.svc.end()));

    head.append(icon, this.titleEl, xBtn);
    this.card.appendChild(head);

    // ── Body ─────────────────────────────────────────────────────────────────
    const body = document.createElement('div');
    css(body, { padding: '14px 16px 10px' });

    this.badgeEl = document.createElement('div');
    css(this.badgeEl, {
      display: 'inline-block', background: PRIMARY, color: '#fff',
      fontSize: '10px', fontWeight: '700', padding: '2px 8px',
      borderRadius: '20px', letterSpacing: '.3px', marginBottom: '8px',
    });

    this.descEl = document.createElement('p');
    css(this.descEl, {
      fontSize: '13px', color: '#475569', lineHeight: '1.6', margin: '0 0 12px',
    });

    this.dotsEl = document.createElement('div');
    css(this.dotsEl, { display: 'flex', gap: '5px', alignItems: 'center' });

    body.append(this.badgeEl, this.descEl, this.dotsEl);
    this.card.appendChild(body);

    // ── Footer ───────────────────────────────────────────────────────────────
    const foot = document.createElement('div');
    css(foot, {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 16px 14px', background: '#f8fafc',
      borderTop: '1px solid #f1f5f9',
    });

    const skip = document.createElement('button') as HTMLButtonElement;
    css(skip, {
      background: 'none', border: 'none', fontSize: '12px',
      color: '#94a3b8', cursor: 'pointer', padding: '0', fontWeight: '500',
    });
    skip.textContent = 'End tour';
    skip.addEventListener('click', () => this.zone.run(() => this.svc.end()));

    const nav = document.createElement('div');
    css(nav, { display: 'flex', alignItems: 'center', gap: '6px' });

    this.prevBtn = document.createElement('button') as HTMLButtonElement;
    css(this.prevBtn, {
      width: '32px', height: '32px', borderRadius: '8px',
      border: '1px solid #e2e8f0', background: '#fff', color: '#475569',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', padding: '0',
    });
    this.prevBtn.innerHTML = '<i class="material-icons" style="font-size:20px">chevron_left</i>';
    this.prevBtn.addEventListener('click', () => this.zone.run(() => this.svc.prev()));

    this.nextBtn = document.createElement('button') as HTMLButtonElement;
    css(this.nextBtn, {
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      padding: '7px 16px', borderRadius: '8px', border: 'none',
      background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DK})`,
      color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    });
    this.nextBtn.addEventListener('click', () => this.zone.run(() => this.svc.next()));

    nav.append(this.prevBtn, this.nextBtn);
    foot.append(skip, nav);
    this.card.appendChild(foot);
  }

  // ─── State handler ───────────────────────────────────────────────────────────

  private onState(s: TourState): void {
    if (!s.active || !s.current) { this.hideAll(); return; }

    this.updateContent(s);

    const id = ++this.stepId;

    // Set a safe default position immediately so card is visible while element is searched
    this.placeCardCenter();

    this.backdrop.style.display = 'block';
    this.ring.style.display     = 'block';
    this.card.style.display     = 'block';

    this.svc.waitForElement(s.current.elementId).then(el => {
      if (id !== this.stepId) return;
      if (el) {
        el.scrollIntoView({ block: 'center' });
        requestAnimationFrame(() => {
          if (id !== this.stepId) return;
          this.positionOnElement(el);
        });
      }
      // if element not found, card already at center from placeCardCenter()
    });
  }

  // ─── Content ─────────────────────────────────────────────────────────────────

  private updateContent(s: TourState): void {
    this.titleEl.textContent = s.current?.title ?? '';
    this.descEl.textContent  = s.current?.description ?? '';
    this.badgeEl.textContent = `${s.step + 1} / ${s.total}`;

    this.dotsEl.innerHTML = '';
    for (let i = 0; i < s.total; i++) {
      const d = document.createElement('span');
      css(d, {
        display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%',
        background: i === s.step ? PRIMARY : '#e2e8f0',
        transform: i === s.step ? 'scale(1.3)' : 'scale(1)',
        transition: 'background .2s, transform .2s',
      });
      this.dotsEl.appendChild(d);
    }

    this.prevBtn.disabled = s.step === 0;
    css(this.prevBtn, { opacity: s.step === 0 ? '.35' : '1', cursor: s.step === 0 ? 'not-allowed' : 'pointer' });

    const last = s.step === s.total - 1;
    this.nextBtn.innerHTML = last
      ? 'Finish <i class="material-icons" style="font-size:18px">check</i>'
      : 'Next <i class="material-icons" style="font-size:18px">chevron_right</i>';
  }

  // ─── Visibility ──────────────────────────────────────────────────────────────

  private hideAll(): void {
    this.backdrop.style.display = 'none';
    this.ring.style.display     = 'none';
    this.card.style.display     = 'none';
  }

  // ─── Positioning ─────────────────────────────────────────────────────────────

  private positionOnElement(el: HTMLElement): void {
    const p  = this.PAD;
    const tw = this.TW;
    const th = this.TH;
    const r  = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const sb = this.SB;
    const mg = 12;

    // Clamp element rect to viewport so huge containers don't produce off-screen coords
    const vTop    = Math.max(0, Math.min(r.top,    vh));
    const vBottom = Math.max(0, Math.min(r.bottom, vh));
    const vMid    = (vTop + vBottom) / 2;

    // Ring: keep at actual element position (shows the real boundary)
    css(this.ring, {
      top:    `${r.top    - p}px`,
      left:   `${r.left   - p}px`,
      width:  `${r.width  + p * 2}px`,
      height: `${r.height + p * 2}px`,
    });

    // ── Sidebar element → card to the right ──────────────────────────────────
    if (r.right < sb + 20) {
      const left   = sb + mg;
      const rawTop = vMid - th / 2;
      const top    = Math.max(mg, Math.min(rawTop, vh - th - mg));
      css(this.card, { top: `${top}px`, left: `${left}px` });
      this.setArrow('left', Math.max(20, vMid - top));
      return;
    }

    // Horizontal: centre on element's visible portion, clamped to main panel
    let left = r.left + r.width / 2 - tw / 2;
    left = Math.max(sb + mg, Math.min(left, vw - tw - mg));
    const arrowOffset = Math.max(20, Math.min(r.left + r.width / 2 - left, tw - 20));

    // ── Try below visible element ─────────────────────────────────────────────
    const belowTop = vBottom + p + mg;
    if (belowTop + th <= vh) {
      css(this.card, { top: `${belowTop}px`, left: `${left}px` });
      this.setArrow('top', arrowOffset);
      return;
    }

    // ── Try above visible element ─────────────────────────────────────────────
    const aboveTop = vTop - p - mg - th;
    if (aboveTop >= 0) {
      css(this.card, { top: `${aboveTop}px`, left: `${left}px` });
      this.setArrow('bottom', arrowOffset);
      return;
    }

    // ── Fallback: centre of screen (element too large / near edges) ───────────
    const centerTop = Math.max(mg, Math.min(vh / 2 - th / 2, vh - th - mg));
    css(this.card, { top: `${centerTop}px`, left: `${left}px` });
    css(this.arrowEl, { display: 'none' });
  }

  /** Place card in the centre of the main-panel area (safe default). */
  private placeCardCenter(): void {
    const left      = Math.max(this.SB + 12, window.innerWidth  / 2 - this.TW / 2);
    const top       = Math.max(12,           window.innerHeight / 2 - this.TH / 2);
    css(this.card, { top: `${top}px`, left: `${left}px` });
    css(this.arrowEl, { display: 'none' });
    // Ring at zero size until element is found
    css(this.ring, { top: '50%', left: '50%', width: '0px', height: '0px' });
  }

  private positionCenter(): void { this.placeCardCenter(); }

  private setArrow(side: 'top' | 'bottom' | 'left', offset: number): void {
    // Reset
    css(this.arrowEl, {
      display: 'block', top: '', bottom: '', left: '', right: '',
      transform: '', borderTop: '', borderBottom: '', borderLeft: '', borderRight: '',
    });

    const t = '10px solid transparent';
    const c = `10px solid ${PRIMARY}`;
    const w = `10px solid #f8fafc`;

    if (side === 'top') {
      css(this.arrowEl, {
        top: '-10px', left: `${offset}px`, transform: 'translateX(-50%)',
        borderLeft: t, borderRight: t, borderBottom: c,
      });
    } else if (side === 'bottom') {
      css(this.arrowEl, {
        bottom: '-10px', left: `${offset}px`, transform: 'translateX(-50%)',
        borderLeft: t, borderRight: t, borderTop: w,
      });
    } else {
      css(this.arrowEl, {
        left: '-10px', top: `${offset}px`, transform: 'translateY(-50%)',
        borderTop: t, borderBottom: t, borderRight: c,
      });
    }
  }
}

// ─── Helper: apply multiple inline styles at once ────────────────────────────
function css(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
  Object.assign(el.style, styles);
}
